'use client';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ClipboardList, GraduationCap, Crown, FolderKanban, UserPlus,
  RotateCcw, BarChart3, MessageSquare, BookOpen, Heart, Clock,
} from 'lucide-react';
import type { RoutineTemplate } from '@/lib/services/routine-template-service';

const ICON_MAP: Record<string, React.ReactNode> = {
  'clipboard-list': <ClipboardList className="h-6 w-6" />,
  'graduation-cap': <GraduationCap className="h-6 w-6" />,
  'crown': <Crown className="h-6 w-6" />,
  'folder-kanban': <FolderKanban className="h-6 w-6" />,
  'user-plus': <UserPlus className="h-6 w-6" />,
  'rotate-ccw': <RotateCcw className="h-6 w-6" />,
  'bar-chart-3': <BarChart3 className="h-6 w-6" />,
  'message-square': <MessageSquare className="h-6 w-6" />,
  'book-open': <BookOpen className="h-6 w-6" />,
  'heart': <Heart className="h-6 w-6" />,
};

const CATEGORY_COLORS: Record<string, string> = {
  engineering: 'bg-blue-500/10 text-blue-500',
  leadership: 'bg-purple-500/10 text-purple-500',
  hr: 'bg-green-500/10 text-green-500',
  sales: 'bg-orange-500/10 text-orange-500',
  training: 'bg-yellow-500/10 text-yellow-500',
  wellbeing: 'bg-pink-500/10 text-pink-500',
  operations: 'bg-slate-500/10 text-slate-500',
};

// Human-facing labels for the "Works with" line. Only room|phone|slack|email
// have wired delivery handlers; the rest may still appear on a template's array.
const CHANNEL_LABELS: Record<string, string> = {
  room: 'Video',
  phone: 'Phone',
  slack: 'Slack',
  email: 'Email',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  calendar: 'Calendar',
};

// Render the common cron shapes our templates use ("Weekdays at 9:00 AM",
// "Fridays at 4:00 PM", "1st & 15th of the month at 10:00 AM", "Daily at 7:00 AM").
// Falls back to the raw expression for anything exotic.
function humanizeCron(cron: string | null): string | null {
  if (!cron) return null;
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return cron;
  const [min, hour, dom, , dow] = parts;
  const h = parseInt(hour, 10);
  const m = parseInt(min, 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return cron;

  const h12 = h % 12 === 0 ? 12 : h % 12;
  const time = `${h12}:${m.toString().padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;

  const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const ordinal = (n: number) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  let when: string;
  if (dow !== '*') {
    if (dow === '1-5') {
      when = 'Weekdays';
    } else {
      const days = dow.split(',').map((d) => {
        const n = parseInt(d, 10);
        return Number.isNaN(n) ? d : DOW[n % 7];
      });
      if (days.length === 1) {
        // Full-name plural for a single day — abbrev+'s' produced "Fris"/
        // "Tues"/"Weds" (adoption UX wave copy fix; also keeps the landing
        // mock's "Fridays at 4 PM" true one click later).
        const DOW_PLURAL = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'];
        const n = parseInt(dow, 10);
        when = Number.isNaN(n) ? `${days[0]}s` : DOW_PLURAL[n % 7];
      } else {
        when = days.join(' & ');
      }
    }
  } else if (dom !== '*') {
    const days = dom.split(',').map((d) => {
      const n = parseInt(d, 10);
      return Number.isNaN(n) ? d : ordinal(n);
    });
    when = `${days.join(' & ')} of the month`;
  } else {
    when = 'Daily';
  }

  return `${when} at ${time}`;
}

interface RoutineTemplateCardProps {
  template: RoutineTemplate;
  onSelect: (slug: string) => void;
}

export function RoutineTemplateCard({ template, onSelect }: RoutineTemplateCardProps) {
  const icon = ICON_MAP[template.icon || ''] || <ClipboardList className="h-6 w-6" />;
  const categoryColor = CATEGORY_COLORS[template.category] || 'bg-muted text-muted-foreground';
  const questionCount = template.default_questions?.length || 0;
  const schedule = humanizeCron(template.default_schedule_cron);
  const channels = (template.action_channels || []).map((c) => CHANNEL_LABELS[c] || c);

  return (
    <Card
      className="cursor-pointer hover:border-primary/50 transition-colors"
      onClick={() => onSelect(template.slug)}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="p-2 rounded-lg bg-muted">{icon}</div>
          <Badge variant="secondary" className={categoryColor}>
            {template.category}
          </Badge>
        </div>
        <CardTitle className="text-lg mt-3">{template.name}</CardTitle>
        <CardDescription className="line-clamp-2">{template.description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 text-sm text-muted-foreground mb-4">
          <div className="flex items-center gap-4">
            <span>{template.default_duration_minutes} min</span>
            <span>{questionCount} questions</span>
          </div>
          {schedule && (
            <div className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 shrink-0" />
              <span>{schedule}</span>
            </div>
          )}
          {channels.length > 0 && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs uppercase tracking-wide">Works with:</span>
              <span>{channels.join(' · ')}</span>
            </div>
          )}
        </div>
        <Button variant="outline" className="w-full" onClick={() => onSelect(template.slug)}>
          Use this template
        </Button>
      </CardContent>
    </Card>
  );
}
